import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, Subscription, interval, takeUntil } from 'rxjs';
import { PatientService, PatientFileExportStatus } from '../../../core/services/patient.service';
import { SocketService } from '../../../core/services/socket.service';
import { FileDownloadService } from '../../../core/services/file-download.service';

export interface ExportProgressDialogData {
  patientId: string;
  exportId: string;
  patientName?: string;
}

/**
 * Watches a queued full-patient-file export and hands the finished zip to the user.
 *
 * The export runs for minutes on the server, so progress arrives over the
 * `export-progress-<exportId>` socket room. A socket that drops mid-export would otherwise
 * leave this spinning forever, so a slow poll of the status endpoint runs alongside it and
 * either channel can complete the dialog.
 */
@Component({
  selector: 'app-export-progress-dialog',
  templateUrl: './export-progress-dialog.component.html',
  styleUrls: ['./export-progress-dialog.component.scss']
})
export class ExportProgressDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private socketSubscription: Subscription | null = null;

  /** How often the fallback poll asks the server, in case the socket never reconnects. */
  private static readonly POLL_INTERVAL_MS = 10000;

  status: 'pending' | 'processing' | 'completed' | 'failed' = 'pending';
  progress = 0;
  message = 'Queued...';
  subMessage = '';
  errorMessage = '';
  fileName: string | null = null;
  fileSize: number | null = null;
  generatedAt: string | null = null;

  downloading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ExportProgressDialogData,
    private dialogRef: MatDialogRef<ExportProgressDialogComponent>,
    private patientService: PatientService,
    private socketService: SocketService,
    private fileDownloadService: FileDownloadService
  ) {
    // Only guards against a stray backdrop click / Escape. The Close button stays enabled
    // throughout — see close().
    this.dialogRef.disableClose = true;
  }

  get isRunning(): boolean {
    return this.status === 'pending' || this.status === 'processing';
  }

  /**
   * What the bar actually shows.
   *
   * ⚠ CAPPED AT 99 WHILE RUNNING. On this dialog "100%" is read as "my file is ready", and the
   * server reserves the last points for the upload — which on a large patient file is minutes
   * of real work. An earlier version let the bar reach 100 with the words "Export ready" while
   * a 744 MB upload still had seven minutes to go, and no download button to press. The bar
   * only completes when the export actually has.
   */
  get displayProgress(): number {
    return this.isRunning ? Math.min(this.progress, 99) : this.progress;
  }

  ngOnInit(): void {
    this.listenForProgress();
    this.startPollingFallback();

    // The worker may already have run ahead of us — pull the current state immediately rather
    // than waiting a full poll interval to find out the export is done.
    this.pollStatus();
  }

  ngOnDestroy(): void {
    this.stopListening();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private listenForProgress(): void {
    const { exportId } = this.data;

    this.socketService.emit('joinExportProgress', { exportId });

    this.socketSubscription = this.socketService
      .listen(`export-progress-${exportId}`, 'patient-file-export')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (typeof event?.progress === 'number') {
          this.progress = event.progress;
        }
        if (event?.message) {
          this.message = event.message;
        }
        if (event?.subMessage !== undefined && event?.subMessage !== null) {
          this.subMessage = event.subMessage;
        }

        if (event?.failed) {
          this.status = 'failed';
          this.errorMessage = event.subMessage || event.message || 'The export failed.';
          this.finish();
          return;
        }

        if (event?.completed) {
          this.status = 'completed';
          this.progress = 100;
          this.fileName = event.fileName ?? this.fileName;
          this.fileSize = event.fileSize ?? this.fileSize;
          this.finish();
          return;
        }

        this.status = 'processing';
      });
  }

  private startPollingFallback(): void {
    interval(ExportProgressDialogComponent.POLL_INTERVAL_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isRunning) {
          this.pollStatus();
        }
      });
  }

  private pollStatus(): void {
    this.patientService
      .getPatientFileExportStatus(this.data.patientId, this.data.exportId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (state: PatientFileExportStatus) => {
          // The socket is the faster channel; only let the poll move things forward so a
          // late-arriving poll response cannot rewind a bar the socket already advanced.
          if (!this.isRunning) return;

          this.status = state.status;
          this.progress = Math.max(this.progress, state.progress ?? 0);
          this.message = state.message || this.message;
          this.subMessage = state.subMessage || this.subMessage;
          this.fileName = state.fileName;
          this.fileSize = state.fileSize;
          this.generatedAt = state.createdAt ?? this.generatedAt;

          if (state.status === 'failed') {
            this.errorMessage = state.errorMessage || 'The export failed.';
            this.finish();
          } else if (state.status === 'completed') {
            this.progress = 100;
            this.finish();
          }
        },
        error: () => {
          // A single failed poll is not worth surfacing — the socket may still deliver, and
          // the next tick will try again.
        }
      });
  }

  private finish(): void {
    this.stopListening();
    this.dialogRef.disableClose = false;
  }

  private stopListening(): void {
    if (this.socketSubscription) {
      this.socketSubscription.unsubscribe();
      this.socketSubscription = null;
    }
    this.socketService.emit('leaveExportProgress', { exportId: this.data.exportId });
  }

  download(): void {
    if (this.downloading) return;
    this.downloading = true;

    this.patientService
      .getPatientFileExportDownloadUrl(this.data.patientId, this.data.exportId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (result) => {
          await this.fileDownloadService.downloadFromUrl(result.url, result.fileName);
          this.downloading = false;
          this.dialogRef.close({ downloaded: true });
        },
        error: (error) => {
          this.downloading = false;
          this.status = 'failed';
          this.errorMessage = error?.error?.message || 'The download link could not be generated. Please try again.';
        }
      });
  }

  /**
   * Always available, even mid-export.
   *
   * The job lives on the server, not in this dialog: closing it stops nothing, and the export
   * can be picked up again from the patient file. Blocking Close while running only means that
   * if the terminal event never arrives — a killed worker, a dropped socket on a long job —
   * the user is trapped in a modal with a bar that will never move.
   */
  close(): void {
    this.dialogRef.close({ downloaded: false, status: this.status });
  }

  /**
   * Discard this export and build a fresh one.
   *
   * Offered whenever an already-finished export is shown, because it may predate notes the
   * user has just written — reattaching must never quietly hand back stale clinical data. The
   * caller queues the new job and reopens this dialog against it.
   */
  regenerate(): void {
    this.dialogRef.close({ downloaded: false, regenerate: true });
  }

  /** "today at 14:32" / "18 Aug at 14:32" — enough to judge whether an export is stale. */
  get generatedAtLabel(): string {
    if (!this.generatedAt) return '';
    const when = new Date(this.generatedAt);
    if (isNaN(when.getTime())) return '';

    const time = when.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    const isToday = when.toDateString() === new Date().toDateString();
    if (isToday) return `today at ${time}`;

    return `${when.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} at ${time}`;
  }
}
